<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import PolarisAssistant from 'dashboard/api/polaris/assistant';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import PolarisMark from 'dashboard/components-next/sidebar/PolarisMark.vue';
import { Skeleton } from 'dashboard/components-next/polaris/ui/skeleton';

// Phase 4.5: the kit's text block — the Polaris mark and its label over the summary, skeleton lines while it loads.
// Numbers and identifiers come in **bold** from the endpoint and stay bold (content rule), in the text's own color.

const props = defineProps({
  range: {
    type: String,
    default: '30',
  },
});

const route = useRoute();
const assistantId = computed(() => route.params.assistantId);

// Markdown summary generated by the model from the assistant's stats (served by
// the polaris/assistants/:id/summary endpoint). Numbers are emphasised with
// **bold** so we can highlight them (see prose-strong styling below).
const welcomeMarkdown = ref('');
const isLoading = ref(false);

const fetchSummary = async () => {
  isLoading.value = true;
  try {
    const { data } = await PolarisAssistant.getSummary({
      assistantId: assistantId.value,
      range: props.range,
    });
    welcomeMarkdown.value = data.message ?? '';
  } catch {
    welcomeMarkdown.value = '';
  } finally {
    isLoading.value = false;
  }
};

watch([() => props.range, assistantId], fetchSummary, { immediate: true });

// Render through the shared markdown formatter (html disabled, so it is safe)
// used everywhere else for Polaris output, instead of a bespoke parser. It
// handles paragraphs, line breaks, links, lists and emphasis consistently.
const formattedSummary = computed(
  () => new MessageFormatter(welcomeMarkdown.value).formattedMessage
);
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <section
    v-if="isLoading || welcomeMarkdown"
    class="grid gap-3"
    data-welcome-card
  >
    <p
      class="flex items-center gap-2 type-polaris-caption text-polaris-muted-foreground"
    >
      <PolarisMark size="xs" />
      {{ $t('POLARIS.OVERVIEW.WELCOME.LABEL') }}
    </p>
    <div
      v-if="isLoading"
      class="grid gap-2.5"
      :aria-label="$t('POLARIS.OVERVIEW.WELCOME.LOADING')"
      aria-busy="true"
    >
      <Skeleton class="h-5 w-full" />
      <Skeleton class="h-5 w-11/12" />
      <Skeleton class="h-5 w-4/6" />
    </div>
    <div
      v-else
      v-dompurify-html="formattedSummary"
      class="prose max-w-none text-polaris-foreground prose-p:mb-3 prose-p:mt-0 prose-p:text-lg prose-p:leading-relaxed prose-p:text-polaris-foreground last:prose-p:mb-0 prose-a:text-polaris-foreground prose-strong:font-bold prose-strong:tabular-nums prose-strong:text-polaris-foreground"
    />
  </section>
</template>
